import * as Yup from 'yup';

const isValidCardNumber = (cardNumber: string): boolean => {
  const cleaned = cardNumber.replace(/\s/g, '');
  if (!/^\d{13,19}$/.test(cleaned)) return false;

  let sum = 0;
  let isEven = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

const isValidExpiryDate = (expiry: string): boolean => {
  const cleaned = expiry.replace(/\s|\//g, '');
  if (cleaned.length !== 4) return false;

  const month = parseInt(cleaned.substring(0, 2), 10);
  const year = parseInt(`20${cleaned.substring(2, 4)}`, 10);

  if (month < 1 || month > 12) return false;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (year < currentYear) return false;
  if (year === currentYear && month < currentMonth) return false;

  return true;
};

export const cardFormSchema = Yup.object().shape({
  pan: Yup.string()
    .required('Card number is required')
    .test('valid-card', 'Please enter a valid card number', isValidCardNumber),
  expiryDate: Yup.string()
    .required('Expiry date is required')
    .test('valid-expiry', 'Please enter a valid expiry date', isValidExpiryDate),
  cvv: Yup.string()
    .required('CVV is required')
    .matches(/^\d{3,4}$/, 'CVV must be 3 or 4 digits'),
});

export type CardType = 'visa' | 'mastercard' | 'verve' | 'unknown';

export const detectCardType = (cardNumber: string): CardType => {
  const cleaned = cardNumber.replace(/\s/g, '');
  if (/^4/.test(cleaned)) return 'visa';
  if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return 'mastercard';
  if (/^506099|^507865|^65002[0-7]/.test(cleaned)) return 'verve';
  return 'unknown';
};

export const formatCardNumber = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const groups = cleaned.match(/.{1,4}/g);
  return groups ? groups.join(' ') : '';
};

export const formatExpiryDate = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length >= 2) {
    return `${cleaned.substring(0, 2)} / ${cleaned.substring(2, 4)}`;
  }
  return cleaned;
};
